import {useParams, useNavigate} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Info,
  RefreshCw,
} from 'lucide-react';
import {format} from 'date-fns';

export function JobRunDetail() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();

  const {
    data: jobRun,
    isLoading,
    refetch,
    isFetching,
  } = trpc.internal.jobRun.getById.useQuery(
    {id: id!},
    {
      enabled: !!id,
      refetchInterval: (data) => (data?.status === 'RUNNING' ? 2000 : false),
    },
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <Badge className='bg-emerald text-white border-0 font-medium text-base px-4 py-2'>
            <CheckCircle2 className='h-4 w-4 mr-2' />
            Completed
          </Badge>
        );
      case 'FAILED':
        return (
          <Badge className='bg-red-500 text-white border-0 font-medium text-base px-4 py-2'>
            <XCircle className='h-4 w-4 mr-2' />
            Failed
          </Badge>
        );
      case 'RUNNING':
        return (
          <Badge className='bg-brand-blue text-white border-0 font-medium text-base px-4 py-2'>
            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
            Running
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge className='bg-brand-gray text-white border-0 font-medium text-base px-4 py-2'>
            <Clock className='h-4 w-4 mr-2' />
            Pending
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${(ms / 60000).toFixed(2)}m`;
  };

  const getLogLevelIcon = (level: string) => {
    switch (level) {
      case 'ERROR':
        return <XCircle className='h-4 w-4 text-red-500' />;
      case 'WARN':
        return <AlertTriangle className='h-4 w-4 text-amber' />;
      default:
        return <Info className='h-4 w-4 text-brand-gray' />;
    }
  };

  const getLogLevelClass = (level: string) => {
    switch (level) {
      case 'ERROR':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'WARN':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className='p-8'>
        <Skeleton className='h-10 w-64 mb-4' />
        <Skeleton className='h-48 w-full mb-4' />
        <Skeleton className='h-96 w-full' />
      </div>
    );
  }

  if (!jobRun) {
    return (
      <div className='p-8'>
        <Card>
          <CardContent className='py-12 text-center'>
            <p className='text-brand-gray'>Job run not found</p>
            <Button variant='outline' className='mt-4' onClick={() => navigate('/jobs')}>
              Back to Jobs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='px-8 py-6 max-w-7xl mx-auto'>
        {/* Back Button */}
        <Button
          variant='ghost'
          className='mb-4 text-brand-gray hover:text-brand-navy'
          onClick={() => navigate('/jobs')}
        >
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to Jobs
        </Button>

        {/* Header Card */}
        <Card className='border-gray-200 shadow-sm mb-6'>
          <CardHeader className='bg-gray-50 border-b'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <CardTitle className='text-2xl font-bold text-brand-navy'>
                  {jobRun.jobName}
                </CardTitle>
                {getStatusBadge(jobRun.status)}
              </div>
              <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Started</p>
                <p className='font-medium text-brand-navy'>
                  {format(new Date(jobRun.startedAt), 'MMM d, yyyy h:mm:ss a')}
                </p>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Completed</p>
                <p className='font-medium text-brand-navy'>
                  {jobRun.completedAt
                    ? format(new Date(jobRun.completedAt), 'MMM d, yyyy h:mm:ss a')
                    : '-'}
                </p>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Duration</p>
                <p className='font-medium font-mono text-brand-navy'>
                  {formatDuration(jobRun.durationMs)}
                </p>
              </div>
              <div>
                <p className='text-sm text-brand-gray mb-1'>Triggered By</p>
                <p className='font-medium text-brand-navy capitalize'>
                  {jobRun.triggeredBy || 'manual'}
                </p>
              </div>
            </div>

            {/* Error Message */}
            {jobRun.errorMessage && (
              <div className='mt-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
                <div className='flex items-start gap-2'>
                  <XCircle className='h-5 w-5 text-red-500 mt-0.5' />
                  <div>
                    <p className='font-medium text-red-800'>Error</p>
                    <p className='text-red-700 font-mono text-sm mt-1'>{jobRun.errorMessage}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Input/Output */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
          <Card className='border-gray-200 shadow-sm'>
            <CardHeader className='bg-gray-50 border-b'>
              <CardTitle className='text-lg font-semibold text-brand-navy'>Input Data</CardTitle>
            </CardHeader>
            <CardContent className='p-4'>
              <pre className='bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono max-h-64'>
                {JSON.stringify(jobRun.inputData, null, 2) || 'null'}
              </pre>
            </CardContent>
          </Card>

          <Card className='border-gray-200 shadow-sm'>
            <CardHeader className='bg-gray-50 border-b'>
              <CardTitle className='text-lg font-semibold text-brand-navy'>Output Data</CardTitle>
            </CardHeader>
            <CardContent className='p-4'>
              <pre className='bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono max-h-64'>
                {JSON.stringify(jobRun.outputData, null, 2) || 'null'}
              </pre>
            </CardContent>
          </Card>
        </div>

        {/* Error/Warn Logs */}
        {jobRun.logs && jobRun.logs.length > 0 && (
          <Card className='border-gray-200 shadow-sm mb-6'>
            <CardHeader className='bg-gray-50 border-b'>
              <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                <AlertTriangle className='h-5 w-5 text-amber' />
                Errors & Warnings ({jobRun.logs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className='p-4'>
              <div className='space-y-2'>
                {jobRun.logs.map((log) => (
                  <div
                    key={log.id}
                    className={`p-3 rounded-lg border ${getLogLevelClass(log.level)}`}
                  >
                    <div className='flex items-start gap-2'>
                      {getLogLevelIcon(log.level)}
                      <div className='flex-1'>
                        <div className='flex items-center justify-between'>
                          <span className='font-medium'>{log.level}</span>
                          <span className='text-xs opacity-70'>
                            {format(new Date(log.timestamp), 'h:mm:ss.SSS a')}
                          </span>
                        </div>
                        <p className='mt-1 font-mono text-sm'>{log.message}</p>
                        {log.data && (
                          <pre className='mt-2 text-xs bg-white/50 p-2 rounded-sm overflow-auto'>
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Full Log Summary */}
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='bg-gray-50 border-b'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>Full Log Output</CardTitle>
          </CardHeader>
          <CardContent className='p-4'>
            <pre className='bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono max-h-96 whitespace-pre-wrap'>
              {jobRun.logSummary || 'No logs available'}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
